// src/components/cart/CartItem.jsx
import React from 'react';
import { formatCurrency } from '../../utils/helpers';
import { getSizeLabel } from '../../utils/constants';
import Button from '../common/Button';

/**
 * Cart Item Component
 * 
 * ⭐ DISPLAYS CUSTOM NAME if provided
 * 
 * Shows:
 * - Product image and details
 * - Selected size
 * - Custom name for printing
 * - Quantity controls
 * - Price and subtotal
 * - Remove button
 */
const CartItem = ({ item, onUpdateQuantity, onRemove, loading }) => {
  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      onUpdateQuantity(item.id, newQuantity);
    }
  };
  const selectedSizeStock = item.product.availableSizes.find(
  (ps) => ps.size === item.size
)?.stockQuantity || 0;

  
  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4">
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="flex-shrink-0">
          <img
            src={item.product.imageUrls?.[0] || '/placeholder-product.png'}
            alt={item.product.name}
            className="w-24 h-24 object-cover rounded-md"
          />
        </div>
        
        {/* Product Details */}
        <div className="flex-grow">
          <div className="flex justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {item.product.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {item.product.category === 'T_SHIRT' ? 'T-Shirt' : 'Hoodie'}
              </p>
              
              {/* Size */}
              <p className="text-sm text-gray-700 mt-2">
                <span className="font-medium">Size:</span> {getSizeLabel(item.size)}
              </p>
              
              {/* ⭐ Custom Name Display - THE KEY FEATURE */}
              {item.customName && (
                <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-1">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                  </svg>
                  <span className="text-sm">
                    <span className="font-medium text-blue-900">Print Name:</span>{' '}
                    <span className="text-blue-700 font-semibold tracking-wide">
                      {item.customName}
                    </span>
                  </span>
                </div>
              )}

{/* Passing Year Display */}
{item.passingYear && (
  <div className="mt-2 block bg-orange-50 border border-orange-200 rounded-md px-3 py-1">
    <span className="text-sm text-orange-800 font-bold">
      Graduation Year: {item.passingYear}
    </span>
  </div>
)}
              
              {/* Price */}
              <p className="text-lg font-bold text-gray-900 mt-3">
                {formatCurrency(item.price)}
                <span className="text-sm text-gray-500 font-normal"> each</span>
              </p>
            </div>
            
            {/* Remove Button */}
            <button
              onClick={() => onRemove(item.id)}
              disabled={loading}
              className="text-red-600 hover:text-red-700 transition-colors h-8"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
          
          {/* Quantity Controls */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleQuantityChange(item.quantity - 1)}
                disabled={loading || item.quantity <= 1}
                className="w-8 h-8 flex items-center justify-center border-2 border-gray-300 rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                −
              </button>
              
              <span className="w-12 text-center font-semibold text-gray-900">
                {item.quantity}
              </span>
              
              <button
                onClick={() => handleQuantityChange(item.quantity + 1)}
                disabled={loading || item.quantity >= selectedSizeStock}
                className="w-8 h-8 flex items-center justify-center border-2 border-gray-300 rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            
            {/* Subtotal */}
            <div className="ml-auto">
              <p className="text-sm text-gray-600">Subtotal</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(item.subtotal)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;